import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Sun, Moon, Share2, Check, Trophy, Camera, AlertTriangle, Crown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BASKI_ORDER = ["Süreç girilmedi", "Üretim Başladı", "Üretim Tamamlandı", "Alana Sevk Edildi", "Montaj Yapıldı", "İcmal Dosyası Çıktı", "Faturası Kesildi"];
const PALETTE = ["#38bdf8", "#60a5fa", "#818cf8", "#a78bfa", "#c084fc", "#e879f9", "#f472b6", "#fb7185", "#fb923c", "#fbbf24", "#a3e635", "#34d399"];
const PAGE_SIZE = 25;
const ls = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };

export default function SetupTvPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(new Date());
  const [theme, setTheme] = useState(() => ls("tv_theme", "light"));
  const [mode, setMode] = useState(() => ls("tv_mode", "pct"));
  const [zone, setZone] = useState(() => ls("tv_zone", "Green Zone"));
  const [page, setPage] = useState(0);
  const [copied, setCopied] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [scale, setScale] = useState(1);
  const pageRef = useRef(0);
  const contentRef = useRef(null);

  useEffect(() => {
    const compute = () => {
      const el = contentRef.current;
      if (!el) return;
      if (window.innerWidth < 1024) { setScale(1); return; }
      const avail = window.innerHeight - ((d?.issues || []).length ? 48 : 0);
      const h = el.scrollHeight;
      setScale(h > avail ? Math.max(0.45, avail / h) : 1);
    };
    const raf = requestAnimationFrame(compute);
    window.addEventListener("resize", compute);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", compute); };
  }, [d, mode, page]);

  const heroCount = d?.week_heroes?.length || 0;
  useEffect(() => {
    if (heroCount <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroCount), 8000);
    return () => clearInterval(t);
  }, [heroCount]);

  const share = async () => {
    const url = `${window.location.origin}/setupdashboard`;
    try { await navigator.clipboard.writeText(url); } catch {
      const ta = document.createElement("textarea"); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  useEffect(() => { try { localStorage.setItem("tv_theme", theme); localStorage.setItem("tv_mode", mode); localStorage.setItem("tv_zone", zone); } catch {} }, [theme, mode, zone]);

  useEffect(() => {
    const load = () => axios.get(`${API}/api/tv/setup`, { params: { zone } }).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(e.response?.data?.detail || "Bağlantı hatası"));
    load();
    const t = setInterval(load, 60000);
    const c = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [zone]);

  const totalPages = Math.max(1, Math.ceil((d?.firms?.length || 0) / PAGE_SIZE));
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => {
    if (totalPages <= 1) return;
    const r = setInterval(() => setPage((pageRef.current + 1) % totalPages), 12000);
    return () => clearInterval(r);
  }, [totalPages]);
  useEffect(() => { if (page >= totalPages) setPage(0); }, [totalPages, page]);

  const dark = theme === "dark";
  const T = useMemo(() => dark ? {
    bg: "bg-slate-950", text: "text-slate-100", card: "bg-slate-900 border-slate-800", muted: "text-slate-500",
    sub: "text-slate-400", bar: "bg-slate-800", label: "text-slate-300", firm: "text-slate-200", tile: "bg-slate-800/60",
    btnOn: "bg-sky-500 text-white", btnOff: "bg-slate-800 text-slate-400 hover:text-slate-200", kpi: "#e2e8f0",
  } : {
    bg: "bg-slate-100", text: "text-slate-900", card: "bg-white border-slate-200 shadow-sm", muted: "text-slate-400",
    sub: "text-slate-500", bar: "bg-slate-200", label: "text-slate-600", firm: "text-slate-700", tile: "bg-slate-100",
    btnOn: "bg-sky-600 text-white", btnOff: "bg-slate-200 text-slate-500 hover:text-slate-800", kpi: "#0f172a",
  }, [dark]);

  if (err) return <div className={`min-h-screen ${T.bg} flex items-center justify-center text-red-500 text-2xl font-bold`} data-testid="tv-error">{err}</div>;
  if (!d) return <div className={`min-h-screen ${T.bg} flex items-center justify-center ${T.sub} text-2xl`} data-testid="tv-loading">Yükleniyor...</div>;

  const kpis = [
    ["Toplam Proje", d.total, T.kpi],
    ["Kurulum Başlayan", `${d.started}`, "#0ea5e9", `%${d.total ? Math.round((d.started / d.total) * 100) : 0} · ${d.total - d.started} başlamadı`],
    ["Tamamlanan", d.done, "#10b981"],
    ["Toplam m²", d.total_m2.toLocaleString("tr-TR"), T.kpi],
    ["Başlayan m²", d.started_m2.toLocaleString("tr-TR"), "#0ea5e9"],
  ];
  const firmsPage = d.firms.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const issues = d.issues || [];
  const tickerText = issues.map((i) => `⚠ ${i.project}${i.stand_no ? ` (${i.stand_no})` : ""}: ${i.text}`).join("      •      ");

  const Toggle = ({ value, set, opts, testid }) => (
    <div className="flex rounded-full overflow-hidden border border-transparent" data-testid={testid}>
      {opts.map(([v, lbl]) => (
        <button key={v} onClick={() => set(v)} data-testid={`${testid}-${v}`}
          className={`px-3 py-1.5 text-xs font-bold transition-colors ${value === v ? T.btnOn : T.btnOff}`}>{lbl}</button>
      ))}
    </div>
  );

  return (
    <div className={`min-h-screen ${T.bg} ${T.text} font-heading ${scale < 1 ? "h-screen overflow-hidden" : ""}`} data-testid="setup-tv-page">
      <style>{`@keyframes tv-marquee { 0% { transform: translateX(100vw); } 100% { transform: translateX(-100%); } }
@keyframes tv-hero-fade { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }`}</style>
      <div ref={contentRef} className="p-4 sm:p-6 pb-24 lg:pb-16"
        style={scale < 1 ? { transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%` } : undefined}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-sky-500 text-sm font-bold tracking-[0.3em] uppercase">GettProposal · COP31</div>
          <h1 className="text-2xl sm:text-4xl font-extrabold mt-1">SETUP TV — Kurulum Durumu</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={share} data-testid="tv-share-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${copied ? "bg-emerald-500 text-white" : T.btnOff}`}>
            {copied ? <Check size={14} /> : <Share2 size={14} />}{copied ? "KOPYALANDI" : "PAYLAŞ"}
          </button>
          <Toggle value={zone} set={setZone} testid="tv-zone-toggle" opts={[["Green Zone", "GREEN ZONE"], ["Blue Zone", "BLUE ZONE"]]} />
          <Toggle value={mode} set={setMode} testid="tv-mode-toggle" opts={[["pct", "%"], ["adet", "ADET"]]} />
          <button onClick={() => setTheme(dark ? "light" : "dark")} data-testid="tv-theme-toggle"
            className={`p-2 rounded-full ${T.btnOff}`}>{dark ? <Sun size={16} /> : <Moon size={16} />}</button>
          <div className="text-right ml-2">
            <div className="text-3xl font-extrabold tabular-nums" data-testid="tv-clock">{now.toLocaleTimeString("tr-TR")}</div>
            <div className={`text-xs ${T.muted}`}>60 sn'de bir otomatik yenilenir</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 mt-6">
        {kpis.map(([lbl, val, color, sub]) => (
          <div key={lbl} className={`${T.card} border p-3 sm:p-5`} data-testid={`tv-kpi-${lbl}`}>
            <div className={`text-[10px] sm:text-[11px] uppercase tracking-widest ${T.muted} font-bold`}>{lbl}</div>
            <div className="text-3xl sm:text-5xl font-extrabold tabular-nums mt-2" style={{ color }}>{val}</div>
            {sub && <div className={`text-xs ${T.sub} mt-1 tabular-nums`}>{sub}</div>}
          </div>
        ))}
      </div>
      <div className={`${T.card} border p-4 sm:p-5 mt-4`} data-testid="tv-overall-progress">
        <div className="flex items-center justify-between">
          <span className={`text-[11px] uppercase tracking-widest ${T.muted} font-bold`}>Genel Kurulum İlerlemesi</span>
          <span className={`text-2xl sm:text-3xl font-extrabold tabular-nums ${(d.overall_pct ?? 0) >= 100 ? "text-emerald-500" : ""}`}>%{(d.overall_pct ?? 0).toLocaleString("tr-TR")}</span>
        </div>
        <div className={`mt-3 h-3 ${T.bar} overflow-hidden`}>
          <div className={`h-full transition-all duration-700 ${(d.overall_pct ?? 0) >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${d.overall_pct ?? 0}%` }} />
        </div>
        <p className={`text-[11px] ${T.muted} mt-2`}>Kapsamdaki tüm firmaların kurulum ilerlemesinin ortalaması (0-%100)</p>
      </div>
      {heroCount > 0 && (() => {
        const h = d.week_heroes[heroIdx % heroCount];
        return (
          <div className="mt-4 relative overflow-hidden border p-4 sm:p-5 flex items-center gap-4"
            style={{ background: dark ? "linear-gradient(90deg, #451a03, #78350f 55%, #0f172a)" : "linear-gradient(90deg, #fffbeb, #fef3c7 55%, #ffffff)", borderColor: dark ? "#92400e" : "#fcd34d" }}
            data-testid="tv-week-hero">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shrink-0 shadow-lg" style={{ background: "linear-gradient(135deg, #fbbf24, #d97706)" }}>
              <Crown size={26} className="text-white" />
            </div>
            <div key={h.name} className="min-w-0" style={{ animation: "tv-hero-fade 0.6s ease" }}>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] font-extrabold text-amber-600">Haftanın Kahramanı</div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-xl sm:text-3xl font-extrabold truncate">{h.name}</span>
                <span className={`text-xs sm:text-sm font-bold tabular-nums ${T.sub}`}>
                  bu hafta {h.photos > 0 && `${h.photos} fotoğraf`}{h.photos > 0 && h.issues > 0 && " · "}{h.issues > 0 && `${h.issues} sorun bildirimi`}
                </span>
              </div>
            </div>
            {heroCount > 1 && (
              <div className="ml-auto flex gap-1.5 shrink-0">
                {d.week_heroes.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)} data-testid={`tv-hero-dot-${i}`}
                    className="w-2 h-2 rounded-full transition-colors" style={{ background: i === heroIdx % heroCount ? "#d97706" : (dark ? "#78350f" : "#fde68a") }} />
                ))}
              </div>
            )}
            <Trophy size={90} className="absolute -right-4 -bottom-5 opacity-10 text-amber-600 pointer-events-none" />
          </div>
        );
      })()}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className={`${T.card} border p-5`} data-testid="tv-kurulum">
          <div className={`text-[11px] uppercase tracking-widest ${T.muted} font-bold mb-4`}>Kurulum Süreç Raporu · adımı tamamlayan proje</div>
          <div className="overflow-x-auto pb-1">
            <div className="min-w-max">
              <div className="flex items-end gap-1 h-48 justify-start" data-testid="tv-kurulum-chart">
                {d.kurulum_items.map((st, i) => {
                  const n = d.kurulum_counts[st];
                  const maxV = Math.max(1, ...Object.values(d.kurulum_counts));
                  return (
                    <div key={st} className="w-14 shrink-0 flex flex-col items-center justify-end h-full">
                      <div className="text-[11px] font-extrabold tabular-nums mb-1" style={{ color: n ? PALETTE[i % PALETTE.length] : undefined, opacity: n ? 1 : 0.35 }}>
                        {mode === "pct" ? `%${d.total ? Math.round((n / d.total) * 100) : 0}` : n}
                      </div>
                      <div className={`w-3 rounded-full ${T.bar} flex items-end overflow-hidden`} style={{ height: "calc(100% - 20px)" }}>
                        <div className="w-full rounded-full transition-all duration-700" style={{ height: `${Math.max(n ? 4 : 0, (n / maxV) * 100)}%`, background: `linear-gradient(to top, ${PALETTE[i % PALETTE.length]}, ${PALETTE[i % PALETTE.length]}cc)` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-1 mt-2 justify-start">
                {d.kurulum_items.map((st, i) => (
                  <div key={st} className={`w-14 shrink-0 text-center text-[8px] leading-tight font-semibold ${T.sub}`}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full mb-0.5" style={{ background: PALETTE[i % PALETTE.length] }} /><br />{st}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={`text-[11px] uppercase tracking-widest ${T.muted} font-bold mt-5 mb-2`}>Kurulum Süreci · KPI</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="tv-kurulum-kpis">
            {[["Kurulum başlamadı", d.total - d.started, dark ? "#64748b" : "#94a3b8"], ...d.kurulum_items.map((st, i) => [st, d.kurulum_counts[st], PALETTE[i % PALETTE.length]])].map(([lbl, n, color]) => (
              <div key={lbl} className={`${T.tile} p-2`}>
                <div className="flex items-baseline gap-1">
                  <span className="text-base font-extrabold tabular-nums" style={{ color }}>
                    {mode === "pct" ? `%${d.total ? Math.round((n / d.total) * 100) : 0}` : <>{n}<span className={`text-[9px] font-semibold ${T.muted}`}>/{d.total}</span></>}
                  </span>
                  <span className={`text-[9px] font-bold ${T.muted} tabular-nums`}>{mode === "pct" ? n : `%${d.total ? Math.round((n / d.total) * 100) : 0}`}</span>
                </div>
                <div className={`text-[9px] ${T.sub} font-semibold leading-tight mt-0.5 line-clamp-2`} title={lbl}>{lbl}</div>
                <div className={`mt-1 h-1 ${T.bar} overflow-hidden`}>
                  <div className="h-full" style={{ width: `${d.total ? Math.round((n / d.total) * 100) : 0}%`, background: color }} />
                </div>
              </div>
            ))}
          </div>
          <div className={`text-[11px] uppercase tracking-widest ${T.muted} font-bold mt-6 mb-3`}>Baskı Süreci</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {BASKI_ORDER.filter((k) => d.baski_counts[k] != null).map((k) => (
              <div key={k} className={`${T.tile} p-3`}>
                <div className="text-2xl font-extrabold tabular-nums text-amber-500">
                  {mode === "pct" ? `%${d.total ? Math.round((d.baski_counts[k] / d.total) * 100) : 0}` : d.baski_counts[k]}
                </div>
                <div className={`text-[10px] ${T.sub} font-semibold leading-tight mt-0.5`}>{k}</div>
              </div>
            ))}
          </div>
          {((d.top_uploaders || []).length > 0 || (d.top_reporters || []).length > 0) && (
            <>
              <div className={`text-[11px] uppercase tracking-widest ${T.muted} font-bold mt-6 mb-3 flex items-center gap-1.5`}>
                <Trophy size={13} className="text-amber-500" /> Saha Kahramanları · En Çok Katkı Sağlayanlar
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tv-leaderboard">
                {[["En Çok Fotoğraf Yükleyen", d.top_uploaders || [], "photos", Camera, "foto"], ["En Çok Sorun Bildiren", d.top_reporters || [], "issues", AlertTriangle, "sorun"]].map(([title, list, key, Icon, unit]) => (
                  <div key={title} className={`${T.tile} p-3`}>
                    <div className={`text-[10px] uppercase tracking-wider ${T.muted} font-bold mb-2 flex items-center gap-1`}><Icon size={11} />{title}</div>
                    {list.map((c, i) => (
                      <div key={c.name} className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold shrink-0"
                          style={{ background: ["#fbbf24", "#cbd5e1", "#d97706"][i] || "transparent", color: i < 3 ? "#1e293b" : (dark ? "#94a3b8" : "#64748b") }}>{i + 1}</span>
                        <span className={`flex-1 text-xs font-semibold ${T.firm} truncate`}>{c.name}</span>
                        <span className="text-sm font-extrabold tabular-nums" style={{ color: i === 0 ? "#f59e0b" : undefined }}>{c[key]} <span className={`text-[9px] font-semibold ${T.muted}`}>{unit}</span></span>
                      </div>
                    ))}
                    {list.length === 0 && <div className={`text-[10px] ${T.muted}`}>Henüz kayıt yok.</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className={`${T.card} border p-5 flex flex-col`} data-testid="tv-firms">
          <div className="flex items-center justify-between mb-4">
            <div className={`text-[11px] uppercase tracking-widest ${T.muted} font-bold`}>Firma Bazında Kurulum İlerlemesi ({d.firms.length})</div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1" data-testid="tv-firms-pager">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i} onClick={() => setPage(i)} data-testid={`tv-firms-page-${i + 1}`}
                    className={`w-7 h-7 rounded-full text-xs font-bold transition-colors ${page === i ? T.btnOn : T.btnOff}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2 overflow-hidden flex-1">
            {firmsPage.map((f) => (
              <div key={`${f.name}-${f.stand_no}`} className="flex items-center gap-3">
                <div className={`w-28 sm:w-56 text-xs font-semibold ${T.firm} truncate text-right`}>{f.name}</div>
                <div className={`flex-1 h-5 ${T.bar}`}>
                  <div className="h-full" style={{ width: `${f.pct}%`, background: f.pct === 100 ? "#10b981" : "#0ea5e9" }} />
                </div>
                <div className={`w-14 text-lg font-extrabold tabular-nums ${f.pct === 100 ? "text-emerald-500" : "text-sky-500"}`}>
                  {mode === "pct" ? `%${f.pct}` : `${f.done_steps}/${d.total_steps}`}
                </div>
              </div>
            ))}
            {d.firms.length === 0 && <div className={`${T.muted} text-sm`}>Henüz kurulumu başlayan firma yok.</div>}
          </div>
        </div>
      </div>
      </div>
      {issues.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 flex items-stretch h-12 z-50" data-testid="tv-issue-ticker">
          <div className="bg-red-600 text-white flex items-center gap-2 px-3 sm:px-5 font-extrabold text-xs sm:text-sm tracking-widest shrink-0">
            <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" /><span className="hidden sm:inline">CANLI · SORUN BİLDİRİMLERİ ({issues.length})</span><span className="sm:hidden">CANLI ({issues.length})</span>
          </div>
          <div className={`flex-1 overflow-hidden flex items-center ${dark ? "bg-slate-900 border-t border-slate-800" : "bg-white border-t border-slate-200"}`}>
            <div className="whitespace-nowrap font-semibold text-sm" style={{ animation: `tv-marquee ${Math.max(30, issues.length * 12)}s linear infinite` }}>
              {tickerText}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
